import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import dayjs from "dayjs";
import { useCurrentEvent } from "@/lib/event-context";
import { getDashboardKpis, listOrders, getPaymentMethodStats } from "@/lib/api";
import { fmtEuro, statusLabel, fmtRelative } from "@/lib/formatters";

const METHOD_LABEL: Record<string, string> = {
  ideal: "iDEAL",
  creditcard: "Creditcard",
  bancontact: "Bancontact",
  applepay: "Apple Pay",
  paypal: "PayPal",
  banktransfer: "Bankoverschrijving",
  tap_manual: "Tap/PIN",
  sofort: "SOFORT",
  klarna: "Klarna",
  onbekend: "Onbekend",
};

export function Dashboard() {
  const { data: event, isLoading: evLoading } = useCurrentEvent();
  const eventId = event?.id;

  const { data: kpis } = useQuery({
    queryKey: ["dashboard", eventId],
    queryFn: () => getDashboardKpis(eventId!),
    enabled: !!eventId,
  });

  const { data: recent } = useQuery({
    queryKey: ["orders", "recent", eventId],
    queryFn: () => listOrders({ eventId: eventId!, limit: 5 }),
    enabled: !!eventId,
  });

  const { data: methods } = useQuery({
    queryKey: ["payment-methods", eventId],
    queryFn: () => getPaymentMethodStats(eventId!, 30),
    enabled: !!eventId,
  });

  const totalMethodCount = (methods ?? []).reduce((s, m) => s + m.count, 0);

  if (evLoading) return <div className="empty"><span className="spinner" /> Laden…</div>;
  if (!event) return <div className="empty">Geen event gevonden. Maak er één aan via /cms/event.</div>;

  const capacity = event.capacity ?? 600;
  const sold = kpis?.tickets_issued ?? 0;
  const scanned = kpis?.tickets_scanned ?? 0;

  return (
    <>
      <div className="kpis">
        <div className="kpi accent">
          <div className="l">Verkocht (tickets)</div>
          <div className="v">{sold} / {capacity}</div>
          <div className="delta">{kpis?.orders_paid ?? 0} betaalde bestellingen</div>
        </div>
        <div className="kpi">
          <div className="l">Omzet</div>
          <div className="v">{fmtEuro(kpis?.revenue_cents ?? 0)}</div>
          <div className="delta">Excl. nog openstaande facturen</div>
        </div>
        <div className="kpi">
          <div className="l">Bestellingen</div>
          <div className="v">{kpis?.orders_total ?? 0}</div>
          <div className="delta">{(kpis?.orders_total ?? 0) - (kpis?.orders_paid ?? 0)} niet-afgerekend</div>
        </div>
        <div className="kpi">
          <div className="l">Gescand bij entree</div>
          <div className="v">{scanned} / {sold}</div>
          <div className="delta">Live tijdens evenement</div>
        </div>
      </div>

      <div className="panel">
        <div className="panel-h">
          <h3>Recente bestellingen</h3>
          <Link to="/orders" className="pill">Bekijk alle →</Link>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Bestelnr</th><th>Klant</th><th>Items</th><th>Bedrag</th><th>Status</th><th>Wanneer</th><th></th>
              </tr>
            </thead>
            <tbody>
              {(recent ?? []).map((o) => (
                <tr key={o.id}>
                  <td><code style={{ color: "var(--c-orange)", fontFamily: "var(--f-mono)" }}>{o.order_no}</code></td>
                  <td>
                    {o.buyer_first_name} {o.buyer_last_name}
                    <br /><small>{o.buyer_email}</small>
                  </td>
                  <td>
                    {(o.items ?? []).map((it, i) => (
                      <span key={i}>{it.qty}× {it.ticket_type?.code ?? "—"}{i < o.items.length - 1 ? ", " : ""}</span>
                    ))}
                  </td>
                  <td>{fmtEuro(o.total_cents)}</td>
                  <td><span className={`pill-st ${o.status}`}>{statusLabel(o.status)}</span></td>
                  <td>{fmtRelative(o.created_at)}</td>
                  <td>
                    <Link to={`/orders/${o.id}`} className="pill">Open →</Link>
                  </td>
                </tr>
              ))}
              {recent && recent.length === 0 && (
                <tr><td colSpan={7} className="empty">Nog geen bestellingen.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="row2" style={{ marginTop: "1.2rem" }}>
        <div className="panel">
          <div className="panel-h">
            <h3>Betaalmethodes (laatste 30 dagen)</h3>
            <span className="pill">{totalMethodCount} betalingen</span>
          </div>
          {totalMethodCount === 0 ? (
            <div className="empty">Nog geen betaalde orders met methode-info.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "0.55rem" }}>
              {methods!.map((m) => {
                const pct = (m.count / totalMethodCount) * 100;
                return (
                  <div key={m.method}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontFamily: "var(--f-mono)", fontSize: "0.78rem", marginBottom: 4 }}>
                      <span style={{ color: "var(--c-cream)" }}>{METHOD_LABEL[m.method] ?? m.method}</span>
                      <span style={{ color: "rgba(255,244,214,0.6)" }}>
                        {m.count} · {fmtEuro(m.cents)} · {pct.toFixed(0)}%
                      </span>
                    </div>
                    <div style={{ height: 6, background: "rgba(255,122,26,0.12)", borderRadius: 3, overflow: "hidden" }}>
                      <div style={{
                        width: `${pct}%`,
                        height: "100%",
                        background: "linear-gradient(90deg, var(--c-orange), var(--c-orange-bright))",
                      }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: "1rem", fontSize: "0.7rem", opacity: 0.55, fontFamily: "var(--f-mono)" }}>
            Bron: Mollie webhook → orders.metadata.payment_method
          </div>
        </div>

        <div className="panel">
          <div className="panel-h">
            <h3>Snel naar</h3>
          </div>
          <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
            <Link to="/scan" className="btn-admin sm">◉ Scanner</Link>
            <Link to="/event-day" className="btn-admin sm">📊 Event-dag</Link>
            <Link to="/orders/new" className="btn-admin ghost sm">+ Handmatig</Link>
            <Link to="/invite" className="btn-admin ghost sm">★ Ambassadeur</Link>
            <Link to="/mailings" className="btn-admin ghost sm">✉ Mailing</Link>
            <Link to="/cms/event" className="btn-admin ghost sm">⚙ Settings</Link>
          </div>
          <div style={{ marginTop: "1rem", fontFamily: "var(--f-mono)", fontSize: "0.72rem", color: "rgba(255,244,214,0.55)", lineHeight: 1.6 }}>
            <div><strong style={{ color: "var(--c-cream)" }}>{event.name}</strong></div>
            <div>{dayjs(event.starts_at).format("dddd D MMMM YYYY · HH:mm")}</div>
            <div>{event.venue}</div>
            <div style={{ marginTop: 4, color: "var(--c-orange)" }}>
              {Math.max(0, dayjs(event.starts_at).diff(dayjs(), "day"))} dagen te gaan
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
